import { Fragment, useEffect, useRef, useState } from "react";
import Image from "@Components/Image";
import Reveal from "@Components/Reveal/Reveal";
// The same lightbox the winners' stories use up the page, so there is one video
// popup on this page rather than two that behave slightly differently.
import StoryModal from "../StoryModal/StoryModal";
import styles from "./ScholarshipCategories.module.scss";
import InlineLottie from "@Components/InlineLottie/InlineLottie";
// The same triangle CreatorMarquee plays with, so the two video affordances on this
// page are one shape rather than two near-identical ones.
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import { pageAsset } from "@Utils/getStaticAssetPath";

// Images come from the static-assets bucket, never `import`ed — see `pageAsset`.
const asset = pageAsset("ScholarshipV2");
const lottieBook = asset("categories-book.json");
const lottieTrophy = asset("categories-trophy.json");
const lottiePen = asset("categories-pen.json");
const lottieRocket = asset("categories-rocket.json");
const playIcon = asset("categories-play.svg");

const featureImg = asset("feature.jpg");

// The programme film behind the feature card's play button.
// https://www.youtube.com/watch?v=UK-cAqUHllc&t=2s — the `t=2s` is carried through
// as the embed's start offset.
const FEATURE_VIDEO_ID = "UK-cAqUHllc";
const FEATURE_VIDEO_START = 2;
const FEATURE_TITLE = "scholarship.v2.categories.feature.title";

/**
 * `scale` equalises APPARENT size, not box size.
 *
 * All four comps are 512x512, but their ink fills different fractions of it, so
 * rendering them all at one size leaves them visually mismatched — the same trap
 * the static SVGs fell into before. The factors below are measured, not chosen:
 * each icon's resting-frame ink was sampled and the scale set to bring every
 * icon to a common ~380/512 ink HEIGHT, since these sit in a row and read as
 * balanced when their heights agree rather than their widths.
 *
 *   book    ink 416x396  ->  380/396 = 0.96
 *   trophy  ink 400x364  ->  380/364 = 1.04
 *   pen     ink 352x373  ->  380/373 = 1.02
 *   rocket  ink 294x416  ->  380/416 = 0.91   (tall and narrow, so held back)
 *
 * ⚠️  trophy.json IS A MODIFIED COPY of the library asset (Iconly animation 4483
 * is the rocket; the trophy is 696). Every animation in Iconly's Reward category
 * is Two-Tone — it carries four secondary layers at 40% opacity, which render as
 * grey handles. The other three marks here are Light style and single-weight, so
 * the trophy arrived visibly the odd one out. Those four layers were lifted to
 * 100%, which is the whole diff. Re-exporting it from the MCP will bring the grey
 * back, so re-apply the lift if you ever refresh this file.
 *
 * The icons also had to be REPLACED, not just animated: the previous set was
 * shuffled against the copy — a globe on "Academic Wizards", a rocket on
 * "All-Star Sports" and a graduation cap on "Creative Geniuses". Each mark now
 * matches the row it labels.
 */
const CATEGORIES = [
  {
    lottie: lottieBook,
    scale: 0.96,
    title: "scholarship.v2.categories.item1.title",
    description: "scholarship.v2.categories.item1.description",
  },
  {
    lottie: lottieTrophy,
    scale: 1.04,
    title: "scholarship.v2.categories.item2.title",
    description: "scholarship.v2.categories.item2.description",
  },
  {
    lottie: lottiePen,
    scale: 1.02,
    title: "scholarship.v2.categories.item3.title",
    description: "scholarship.v2.categories.item3.description",
  },
  {
    lottie: lottieRocket,
    scale: 0.91,
    title: "scholarship.v2.categories.item4.title",
    description: "scholarship.v2.categories.item4.description",
  },
];

/** Gap between one row's one-shot and the next, in ms. */
const STAGGER = 140;

/**
 * Categories section — Figma node 2097:3652.
 *
 * No data-nav-theme="dark": this band was black and marked itself so the shared
 * Navbar swapped to its light logo behind it. It is light grey now, so the header
 * has to keep its dark logo and links — marking it would put a white logo on a
 * near-white ground.
 */
const Categories = () => {
  const { t } = useTranslationHook();
  const [playing, setPlaying] = useState(false);
  /** Row whose mark should be replaying: a hovered row, or one the stagger has reached. */
  const [activeRow, setActiveRow] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /**
   * Play each mark once as the list arrives, one after another.
   *
   * InlineLottie parks on its finished frame and replays on hover, which is the
   * pattern the stats row above uses. On its own that leaves the marks completely
   * still on touch, where there IS no hover — so the list would animate for mouse
   * users and never for anyone on a phone. The one-shot below covers that: it fires
   * once, when the list first reaches the viewport, and then hover takes over.
   */
  useEffect(() => {
    const node = listRef.current;
    if (!node || typeof IntersectionObserver !== "function") return undefined;

    const timers: number[] = [];
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        CATEGORIES.forEach((_, i) => {
          timers.push(
            window.setTimeout(() => setActiveRow(i), i * STAGGER),
            // Release it again so a later hover is a fresh change of `play` rather
            // than a no-op, and so no row stays latched as "playing".
            window.setTimeout(() => setActiveRow(null), i * STAGGER + STAGGER - 10),
          );
        });
      },
      { threshold: 0.35 },
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
      timers.forEach(window.clearTimeout);
    };
  }, []);

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        {/* Three grid cells: heading top-left, then video and list side by side on
            the row below — which is what lines the list up with the video. */}
        <Reveal className={styles.top}>
          <h2 className={styles.heading}>
            {t("scholarship.v2.categories.heading")}
            <br />
            <span className={styles.headingMuted}>
              {t("scholarship.v2.categories.headingMuted")}
            </span>
          </h2>

          <div className={styles.feature}>
            <Image
              src={featureImg}
              alt={t("scholarship.v2.categories.feature.imagealt")}
              className={styles.featureImage}
              width="100%"
              height="100%"
            />

            <div className={styles.caption}>
              <div className={styles.captionText}>
                <span className={styles.captionTitle}>{t(FEATURE_TITLE)}</span>
                <span className={styles.captionMeta}>
                  <span>{t("scholarship.v2.categories.feature.channel")}</span>
                  <span className={styles.captionDot}>·</span>
                  <span>{t("scholarship.v2.categories.feature.duration")}</span>
                </span>
              </div>

              {/* Figma leaves this circle's icon slot empty; it carries a play
                  triangle so the card reads as a video rather than a plain dot.
                  A real button rather than the decorative span it was, since it now
                  opens the film — so it is focusable and answers the keyboard. */}
              <button
                type="button"
                className={styles.captionAction}
                onClick={() => setPlaying(true)}
                aria-label={`${t("scholarship.v2.winners.watchstory")}: ${t(FEATURE_TITLE)}`}
                data-testid="scholarship-v2-feature-play"
              >
                {/* The rotation lives on this slot, not on the Image — Image's own
                    `.animateOpacity.show { transform: none }` cancels a transform set
                    on the img itself. Same reason CreatorMarquee wraps its copy. */}
                <span className={styles.captionPlaySlot} aria-hidden="true">
                  <Image
                    src={playIcon}
                    alt=""
                    className={styles.captionPlay}
                    width={13}
                    height={12}
                    isEagerLoad
                  />
                </span>
              </button>
            </div>
          </div>

          <div className={styles.list} ref={listRef}>
            <span className={styles.listLabel}>{t("scholarship.v2.categories.listlabel")}</span>

            {CATEGORIES.map((category, i) => (
              <Fragment key={category.title}>
                {i > 0 ? <span className={styles.hRule} aria-hidden="true" /> : null}
                <div
                  className={styles.row}
                  onMouseEnter={() => setActiveRow(i)}
                  onMouseLeave={() => setActiveRow(null)}
                >
                  {/* Shared 32px slot; the mark is centred in it and magnified by
                      transform, so changing its apparent size costs no layout and
                      the rows stay on their grid. Hovering anywhere on the ROW
                      replays it — the same reason the stats row drives its mark
                      from the column rather than the 32px mark itself, which would
                      otherwise be a much smaller target than it looks. */}
                  <span className={styles.iconSlot} aria-hidden="true">
                    <InlineLottie
                      src={category.lottie}
                      size={32}
                      scale={category.scale}
                      autoPlay={false}
                      play={activeRow === i}
                    />
                  </span>
                  <div className={styles.rowText}>
                    {/* h3 under this section's h2 — these name the four categories,
                        so they are outline entries rather than styled text. */}
                    <h3 className={styles.rowTitle}>{t(category.title)}</h3>
                    <span className={styles.rowDesc}>{t(category.description)}</span>
                  </div>
                </div>
              </Fragment>
            ))}
          </div>
        </Reveal>
      </div>

      <StoryModal
        open={playing}
        title={t(FEATURE_TITLE)}
        videoId={FEATURE_VIDEO_ID}
        start={FEATURE_VIDEO_START}
        onClose={() => setPlaying(false)}
      />
    </section>
  );
};

export default wrapperHOC(Categories, {
  componentName: "Categories-ScholarshipV2",
  showForChina: true,
});
